import { QUNOMEDICAL_BRAND_ID } from 'constants/brand';
import clsx from 'clsx';
import Markdown from 'components/Shared/Markdown';
import PatientManagerComment from 'components/Shared/PatientManagerComment';
import RichText from 'components/ui/ContentBlocks/RichText';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import GET_TY_TREATMENT_PAGE from 'data/graphql/queries/thankYouPage';
import { GET_TREATMENT_REVIEWS } from 'data/graphql/queries/reviews';
import { getReviews } from 'data/utils/reviews';
import { useTranslation } from 'hooks/useTranslation';
import { fetchContentfulGql } from 'services/contentful';
import { getContentfulLocale } from 'utils/locale';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import Testimonials from './Testimonials';
import FaqSection from './FaqSection';
import BlogPosts from './BlogPosts';
import ImageUpload from './ImageUpload';
import LeadNotFound from './LeadNotFound';
import Steps from './Steps';
import styles from './Page.module.scss';
import type { StepImageUpload } from './ImageUpload';
import type { BlogItem } from 'types/Blog';
import type { Brand } from 'types/Brand';
import type { ReviewItem } from 'types/Review';
import type { Document } from '@contentful/rich-text-types';

type Content = {
  lead: boolean;
  firstName?: string;
  faq?: {
    question: string;
    disableLocale?: boolean;
    answer: string | Document;
  }[];
  posts?: BlogItem[];
  uploads?: {
    blockId: string;
    title?: string;
    subtitle?: string;
    uploads?: {
      items: StepImageUpload[];
    };
  };
  reviews?: ReviewItem[] | null;
  comment?: string;
  introText?: Document;
};

type Props = {
  brand: Brand | null;
};

const ThankYou = ({ brand }: Props): JSX.Element => {
  const router = useRouter();
  const { locale, t } = useTranslation();
  const [content, setContent] = useState<Content | null>(null);

  const { junoid } = router.query;
  const isQunomedical = brand?.sys?.id === QUNOMEDICAL_BRAND_ID;

  useEffect(() => {
    if (!junoid) {
      return;
    }

    const run = async (): Promise<void> => {
      const contentfulLocale = getContentfulLocale(locale) as string;
      const response = await fetch(`/api/lead?jid=${junoid}`);
      const data = await response.json();

      if (!data.hasResult) {
        setContent({ lead: false });
        return;
      }

      const { result } = data || {};

      const treatmentPage = await fetchContentfulGql({
        query: GET_TY_TREATMENT_PAGE,
        variables: {
          locale: contentfulLocale,
          preview: false,
          treatmentName: result.Treatment_Free_Text__c && [
            result.Treatment_Free_Text__c.toLowerCase(),
          ],
        },
      });

      if (!treatmentPage.errors) {
        const { items } = treatmentPage.data.collection;
        const {
          blogPosts,
          onboardingComment,
          faq,
          imageUploads: uploads,
          richTextStart,
          slug,
        } = items[0] || {};

        let reviews = null;
        if (slug) {
          reviews = await getReviews(
            GET_TREATMENT_REVIEWS,
            contentfulLocale,
            slug,
          );
        }

        setContent({
          lead: true,
          firstName: result.FirstName || '',
          faq: faq?.items,
          posts: blogPosts?.items,
          comment: onboardingComment || brand?.onboardingComment,
          introText: richTextStart || brand?.richTextStart,
          reviews,
          uploads,
        });

        return;
      }
    };

    void run();
  }, [junoid]);

  const renderReviewImage = (): JSX.Element | null => {
    if (!brand?.reviewImage) {
      return null;
    }

    const { reviewImage, reviewLink } = brand || {};

    const props = {
      src: reviewImage?.url || '',
      alt: reviewImage?.title || '',
      height: reviewImage?.height,
      width: reviewImage?.width,
      isSvg: reviewImage?.url?.includes('.svg') || false,
    };

    if (reviewLink) {
      return (
        <Link href={reviewLink}>
          <Image {...props} />
        </Link>
      );
    }

    return <Image {...props} />;
  };

  if (content && !content?.lead) {
    return <LeadNotFound />;
  }

  return (
    <>
      <div className={styles.upperContainer}>
        <h2 className={styles.headline}>
          {!content ? (
            <Skeleton height={35} />
          ) : (
            `${content.firstName}, ${t('ty.headline')}`
          )}
        </h2>

        <div
          className={clsx(
            'rounded -mt-1 border',
            !content ? 'border-silverLight' : 'border-secondary',
          )}
        >
          <div className={styles.upperContent}>
            <PatientManagerComment
              comment={content?.comment}
              skeleton={!content}
              isMarkdown
            />

            {content?.introText && (
              <div className="my-4">
                <RichText fields={content.introText} />
              </div>
            )}

            {content?.uploads && (
              <ImageUpload
                blockId="imageupload"
                uploads={content.uploads}
                junoId={junoid as string}
                hideTitles
              />
            )}
          </div>
        </div>
        <Steps
          steps={[
            t('sendRequest'),
            t('getACall'),
            t('receiveAQuote'),
            t('confirmClinic'),
            t('treatment'),
          ]}
          active={1}
          skeleton={!content}
        />
      </div>
      {content && (
        <>
          <div className={styles.introWrapper}>
            <div className={styles.intro}>
              <div>
                <Markdown>{brand?.trustMarkdownText || ''}</Markdown>
              </div>
              <div className={styles.reviewContainer}>
                <div className={styles.reviewWrapper}>
                  {renderReviewImage()}
                </div>
              </div>
            </div>
          </div>

          {isQunomedical && (
            <>
              {content?.reviews && (
                <Testimonials
                  items={content.reviews}
                  hideHeadline
                  noDesktopPadding
                />
              )}
              {content?.posts && (
                <BlogPosts items={content.posts} locale={locale} />
              )}
              {content?.faq && (
                <FaqSection items={content.faq} noDesktopPadding />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ThankYou;
